import React, { memo, useEffect } from 'react';

import { useIntl } from 'react-intl-next';

import Heading from '@atlaskit/heading';
import InfoIcon from '@atlaskit/icon/glyph/info';
import Lozenge from '@atlaskit/lozenge';
import { Box, Inline, Text, xcss } from '@atlaskit/primitives';

import AtlassianIntelligenceIcon from '../atlassian-intelligence-icon';
import { useAnalytics } from '../common/controller/analytics';
import { ErrorBoundary } from '../common/ui/error-boundary';
import { useTenantContext } from '../common/utils/use-tenant-context';
import { KnowledgeBadge } from '../knowledge-card-components';
import { RelatedEntityType } from '../related-entities-api';

import { onRelatedEntityClick, onSurfaceShown } from './analytics';
import { messages } from './messages';
import { useRelatedTopics } from './use-related-topics';

const topicsContainerStyles = xcss({
	borderRadius: '3px',
	boxShadow: 'elevation.shadow.raised',
	marginTop: 'space.200',
});

const topicsSectionStyles = xcss({
	padding: 'space.200',
	borderBottom: '1px solid',
	borderBottomColor: 'color.border',
});

const topicsFooterStyles = xcss({
	paddingBlock: 'space.050',
	paddingInline: 'space.200',
	display: 'flex',
	justifyContent: 'space-between',
});

const infoIconStyles = xcss({
	color: 'color.text.subtlest',
});

type AITopicsByProfileArguments = {
	userId: string;
	cloudId: string;
	userName: string;
};

const AITopicsByProfileComponent = memo(
	({ userId, cloudId, userName }: AITopicsByProfileArguments) => {
		const { formatMessage } = useIntl();
		const [fireAnalyticsEvent] = useAnalytics();
		const { aiRelatedTopics, fetchRelatedTopics, showTopics } = useRelatedTopics();
		const { tenantContext } = useTenantContext({ cloudId });

		const principalEntityType = RelatedEntityType.USER;
		const firstName = userName.split(' ')[0];
		const { cloudUrl } = tenantContext;

		const onBadgeClickHandler = (topicId: string) => {
			fireAnalyticsEvent(
				onRelatedEntityClick({
					clickedEntityId: topicId,
					clickedEntityType: RelatedEntityType.TOPIC,
					principalEntityType,
					principalEntityId: userId,
					location: 'userProfile',
				}),
			);
		};

		useEffect(() => {
			fetchRelatedTopics({ entityId: userId, principalEntityType, cloudId });
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, []);

		useEffect(() => {
			if (showTopics) {
				fireAnalyticsEvent(
					onSurfaceShown({
						principalEntityId: userId,
						principalEntityType,
						relatedTopics: aiRelatedTopics.map((topic) => topic.id),
						location: 'userProfile',
					}),
				);
			}
		}, [showTopics, aiRelatedTopics, userId, fireAnalyticsEvent, principalEntityType]);

		if (!showTopics) {
			return null;
		}

		return (
			<Box testId="ai-topics-by-profile">
				<Heading size="small">
					{formatMessage(messages.ai_topics_surface_header_person, { userName: firstName })}
				</Heading>
				<Box xcss={topicsContainerStyles}>
					<Box xcss={topicsSectionStyles}>
						<Inline space="space.100" rowSpace="space.100" shouldWrap>
							{aiRelatedTopics.map((topic) => (
								<KnowledgeBadge
									key={topic.id}
									name={topic.name}
									url={`${cloudUrl}/wiki/topics/${topic.id}`}
									onBadgeClick={() => onBadgeClickHandler(topic.id)}
								/>
							))}
						</Inline>
					</Box>
					<Box xcss={topicsFooterStyles}>
						<Inline alignBlock="center" space="space.050">
							<Lozenge>{formatMessage(messages.ai_topics_surface_footer_beta_lozenge)}</Lozenge>
							<Box xcss={infoIconStyles}>
								{/* eslint-disable-next-line @atlaskit/design-system/no-legacy-icons */}
								<InfoIcon label="" size="small" />
							</Box>
							<Text size="small" color="color.text.subtlest">
								{formatMessage(messages.ai_topics_surface_footer_content_may_vary)}
							</Text>
						</Inline>
						<Inline alignBlock="center" space="space.050">
							<Text size="small" color="color.text.subtlest">
								{formatMessage(messages.ai_topics_surface_footer_powered_by_ai)}
							</Text>
							<AtlassianIntelligenceIcon greyed />
						</Inline>
					</Box>
				</Box>
			</Box>
		);
	},
);

export const AITopicsByProfile = (props: AITopicsByProfileArguments) => (
	<ErrorBoundary>
		<AITopicsByProfileComponent {...props} />
	</ErrorBoundary>
);
